import { InputLabel, Grid, TextField, SxProps } from '@mui/material'
import Button from '@mui/material/Button'
import { Dayjs } from 'dayjs'
import React, { CSSProperties, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

import ModalActionContainer from 'components/containers/modalActionContainer'
import KanseiAutoComplete from 'components/copied/autoComplete'
import DatePickerForm from 'components/parts/forms/datePickerForm'
import { helper } from 'helper'
import { Option } from 'types/option'
import { ShiftNote } from 'types/shiftNote'

interface Props {
  onClose: () => void
  onAdd: (data: { securityId: string; date: Dayjs; note: string }) => void
  onUpdate: (data: { shiftNote: ShiftNote }) => void
  securities: {
    id: string
    firstName: string
    lastName: string
  }[]
  value?: ShiftNote
}

interface Form {
  securityId: string | null
  date: Dayjs | null
  note: string
}

const shiftNoteToDefaultValues = (shiftNote?: ShiftNote): Form => {
  if (shiftNote == null) {
    return {
      securityId: null,
      date: null,
      note: '',
    }
  }

  return {
    securityId: shiftNote.securityId,
    date: shiftNote.date,
    note: shiftNote.note,
  }
}

export default function NotesModal(props: Props) {
  const { control, handleSubmit } = useForm<Form>({
    defaultValues: shiftNoteToDefaultValues(props.value),
  })
  const getControl = helper.control.valid(control)

  const securityOptions = useMemo(() => {
    return props.securities.map((security) => ({
      value: security.id,
      label: helper.formatter.nameFormat(security),
    }))
  }, [props.securities])

  const submit = (formValue: Form) => {
    if (formValue.securityId == null) return
    if (formValue.date == null) return

    const securityIdValue = formValue.securityId

    const value = {
      securityId: securityIdValue,
      date: formValue.date,
      note: formValue.note,
    }

    if (props.value != null) {
      const shiftNote = {
        ...props.value,
        ...value,
      }
      props.onUpdate({ shiftNote })
      return
    } else {
      props.onAdd(value)
    }
  }

  const handleChange =
    (changer: (value: string | null) => void) => (newValue: Option | null) => {
      if (newValue && typeof newValue.value === 'string') {
        changer(newValue.value)
      } else {
        changer(null)
      }
    }

  const findOption = (value: string | null) => {
    if (value == null) return null
    const option = securityOptions.find((option) => option.value === value)
    if (option == null) return null
    return option
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputLabel>隊員</InputLabel>
          <Controller
            {...getControl('securityId').c}
            render={({ field }) => (
              <KanseiAutoComplete
                id="securityId"
                options={securityOptions}
                value={findOption(field.value)}
                onChange={handleChange(field.onChange)}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <InputLabel>日付</InputLabel>
          <Controller
            {...getControl('date').c}
            render={({ field }) => (
              <DatePickerForm
                sx={datePickerStyle}
                openTo={'day'}
                views={['year', 'month', 'day']}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel>備考</InputLabel>
          <Controller
            {...getControl('note').c}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="備考を入力してください"
                multiline
                rows={4}
                fullWidth
                sx={textareaStyle}
              />
            )}
          />
        </Grid>
      </Grid>
      <ModalActionContainer>
        <Button variant="negative" onClick={props.onClose}>
          キャンセル
        </Button>
        <Button variant="contained" type="submit">
          保存
        </Button>
      </ModalActionContainer>
    </form>
  )
}

const textareaStyle: SxProps = {
  '& .MuiInputBase-root': {
    padding: 0,
  },
}

const datePickerStyle: CSSProperties = {
  height: '35px',
  width: '100%',
}
